import { useQuery } from '@tanstack/react-query';
import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { useState } from 'react';

import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { participantBookingQueryOptions } from '@/hooks/api/use-participant-booking';
import { profileQueryOptions, useProfile } from '@/hooks/api/use-profile';
import { getClient } from '@/utils/get-api-client';

import {
  BookingBlocked,
  BookingPageShell,
} from './-components/booking/booking-page-shell';
import {
  BookingScheduler,
  type SchedulableBooking,
} from './-components/booking/booking-scheduler';

/**
 * Book a session on a moderated project: the legacy
 * `/respondents/surveys/:id/schedule` page. The participant must be
 * INVITED with a calendar on the invitation; anything else shows the same
 * refusal the legacy route sent.
 */
export const Route = createFileRoute(
  '/participants/_authenticated/projects/$projectId/schedule',
)({
  loader: async ({ context, params }) => {
    // The authenticated layout has already turned away anyone without a
    // user; the root context type still admits null.
    const userId = context.user?.id;
    if (userId) {
      await Promise.all([
        context.queryClient.ensureQueryData(
          participantBookingQueryOptions(params.projectId, userId),
        ),
        // Pre-fills the booking phone; loaded up front because the form
        // takes its initial value on mount.
        context.queryClient.ensureQueryData(
          profileQueryOptions(userId, getClient(userId)),
        ),
      ]);
    }
  },
  component: SchedulePage,
});

const authenticatedRoute = getRouteApi('/participants/_authenticated');

function SchedulePage() {
  const { projectId } = Route.useParams();
  const { user } = authenticatedRoute.useRouteContext();
  const booking = useQuery(participantBookingQueryOptions(projectId, user.id));
  const profile = useProfile(user.id).data?.profile;
  // Once a session is booked here, the refetched state is SCHEDULED and the
  // gate below would refuse. The scheduler stays on screen with the state it
  // booked against, so it can show its confirmation as the legacy page did.
  const [booked, setBooked] = useState<SchedulableBooking | null>(null);

  const data = booking.data;
  const bookable =
    data &&
    !data.blockedReason &&
    data.event &&
    !(data.status === 'SCHEDULED' && data.booking)
      ? { ...data, event: data.event }
      : null;
  const shown = booked ?? bookable;

  return (
    <BookingPageShell projectId={projectId} wide>
      {shown ? (
        <BookingScheduler
          projectId={projectId}
          userId={user.id}
          booking={shown}
          participantName={{
            firstName: user.firstName,
            lastName: user.lastName,
          }}
          defaultPhone={profile?.phone ?? undefined}
          defaultCountry={profile?.location?.city?.country}
          onBooked={() => setBooked(shown)}
        />
      ) : !data ? (
        <LoadingIndicator />
      ) : data.blockedReason || !data.event ? (
        <BookingBlocked reason={data.blockedReason ?? 'NO_CALENDAR'} />
      ) : (
        // Already booked: the legacy page refused; the reschedule page is
        // where a booked participant goes.
        <BookingBlocked reason="NOT_INVITED" />
      )}
    </BookingPageShell>
  );
}
